import { Injectable, inject } from '@angular/core';
import { FilterPreferenceLocksService } from '@clubhub/ignite/services/filter-preferences/filter-preference-locks.service';
import { filterPreferenceLockedNote, productFilterGroupTitles } from '@shared/constants/filter-preference-options';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { productFiltersNames } from '@clubhub/constants';
import { errorMessages } from '@clubhub/ignite/models/affordability';
import { IApplicants, IMortgageRequirements } from '@clubhub/ignite/models/products';
import { ProductsSearchService } from '@clubhub/ignite/services/products/products-search.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormFieldType, WrapperType, yesNoOptions } from '@shared/components/formly/formly.config';
import { FormsValidators } from '@shared/components/forms/validators';
import {
  MortgageLenders,
  MortgageType,
  ProductLengthExtended,
  ProductType,
  PropertyType,
  PurchaserType,
  RepaymentMethod,
  RepaymentVehicle,
} from '@shared/models';
import { IgniteHelperService, LtvService } from '@shared/services';
import { LenderService } from '@shared/services/lender.service';
import { map } from 'rxjs';

const secondChargeLoanAmountLabel = 'Second charge loan amount';
const loanAmountLabel = 'Loan amount';
const defaultNumberOfApplicants = 1;

@Injectable({
  providedIn: 'root',
})
export class ProductsFormFieldsResiService {
  private readonly helperService = inject(IgniteHelperService);
  private readonly filterPreferenceLocks = inject(FilterPreferenceLocksService);
  private readonly productsSearchService = inject(ProductsSearchService);
  private readonly lenderService = inject(LenderService);
  private readonly ltvService = inject(LtvService);

  public resetFieldIfEmpty(field: FormlyFieldConfig) {
    if (!field.model) {
      field.formControl?.reset();
    }
  }

  public keyboardEventMarkTouched(field: FormlyFieldConfig, event: KeyboardEvent) {
    const key = event.key;
    if (!['Tab', 'Shift'].includes(key)) {
      field.formControl?.markAsTouched();
    }
  }

  public fields: FormlyFieldConfig[] = [
    {
      key: 'mortgageRequirements',
      wrappers: [WrapperType.FormCard],
      validators: {
        max500PercentLoanValue: {
          expression: FormsValidators.expression(FormsValidators.ltv(500)),
          message: 'Loan amount should not exceed 500% of property value',
          errorPath: 'loanAmount',
        },
        max500PercentPurchasePriceShare: {
          expression: FormsValidators.expression(FormsValidators.lts(500)),
          message: 'Purchase price share should not exceed 500% of loan amount',
          errorPath: 'purchasePriceShare',
        },
        // Handles cases where the value has been pre populated and can be outside of the enum options (MCONE-7475)
        purchaserTypeValidator: {
          expression: FormsValidators.expression(FormsValidators.purchaserType()),
          message: 'Purchaser type must be first time buyer or home mover for purchase mortgage type',
          errorPath: 'purchaserType',
        },
        interestOnlyAmountValidator: {
          expression: (control: AbstractControl) => {
            const repaymentMethodValue = (control as any).controls?.repaymentMethod?.value?.value;
            if (repaymentMethodValue === RepaymentMethod.InterestOnlyPartAndPart) {
              const validator = FormsValidators.expression(
                FormsValidators.greaterThanField<IMortgageRequirements>('interestOnlyAmount', 'loanAmount', true),
              );
              return validator(control);
            }

            return true;
          },
          message: errorMessages.interestOnlyLessThanLoanAmount,
          errorPath: 'interestOnlyAmount',
        },
        productTransferInitialDate: this.helperService.getProductTransferInitialPeriodValidation(),
      },
      fieldGroup: [
        {
          type: FormFieldType.Template,
          template: '<span class="text-colour-red-600 fw-bold">*</span> mandatory fields',
        },
        {
          key: 'mortgageType',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'Mortgage type',
            options: this.getMortgageTypeOptions(),
            required: true,
            change: (field: FormlyFieldConfig) => {
              const { mortgageType } = (field?.model ?? {}) as IMortgageRequirements;
              if (mortgageType === MortgageType.Purchase && this.productsSearchService.model) {
                this.productsSearchService.model.mortgageRequirements.helpToBuy = false;
                this.productsSearchService.updateModel(this.productsSearchService.model);
              }
              const helpToBuyValue = mortgageType === MortgageType.Remortgage || mortgageType === MortgageType.Purchase ? false : null;
              (field.parent?.formControl as UntypedFormGroup).controls?.helpToBuy?.setValue(helpToBuyValue);
            },
          },
        },
        {
          key: 'productTypeExtended',
          type: FormFieldType.Select,
          defaultValue: ProductType.Standard,
          props: {
            label: 'Product type',
            required: true,
            options: [
              { label: 'Standard', value: ProductType.Standard },
              { label: 'Shared ownership', value: ProductType.SharedOwnership },
              { label: 'Right to buy', value: ProductType.RightToBuy },
              { label: 'Shared equity', value: ProductType.SharedEquity },
              { label: 'Self-build', value: ProductType.SelfBuild },
              { label: 'Retirement interest only (RIO)', value: ProductType.RetirementInterestOnly },
              { label: 'Joint borrower-sole proprietor', value: ProductType.JointBorrowerSoleProprietor },
            ],
            change: (field: FormlyFieldConfig, event) => {
              const isRio = (this.helperService.getEnumValue(event.target.value) as ProductType) === ProductType.RetirementInterestOnly;
              if (isRio) {
                (field.parent?.formControl as UntypedFormGroup).controls.repaymentMethod.setValue({ value: RepaymentMethod.InterestOnly });
                (field.parent?.formControl as UntypedFormGroup).controls.helpToBuy?.setValue(false);
              }
            },
          },
        },
        {
          key: 'capitalRaising',
          wrappers: [WrapperType.NoWrappers],
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              !this.helperService.isRemortgageRightToBuy(field.parent?.model?.mortgageType, field.parent?.model?.productTypeExtended),
          },
          fieldGroup: [
            {
              key: 'value',
              type: FormFieldType.RadioButtons,
              defaultValue: false,
              props: {
                label: 'Capital raising',
                helpText: 'Extra borrowing under right to buy is usually only acceptable for home improvements. Check with lender first.',
                options: yesNoOptions,
                required: true,
              },
            },
          ],
        },
        {
          key: 'amountTransferredFromOtherLender',
          type: FormFieldType.Currency,
          defaultValue: 0,
          props: {
            label: 'Current mortgage balance outstanding',
            tooltip: 'What is the outstanding mortgage balance from the previous lender?',
            required: true,
          },
          expressions: {
            hide: field =>
              field.model?.mortgageType !== MortgageType.Remortgage ||
              !field.model?.capitalRaising?.value ||
              field.model?.amountTransferredFromOtherLender === 0,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'purchaserType',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'Purchaser type',
            options: [
              { label: 'First time buyer', value: PurchaserType.FirstTimeBuyer },
              { label: 'Homemover', value: PurchaserType.HomeMover },
            ],
          },
          expressions: {
            hide: field => field.parent?.model?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'newBuild',
          type: FormFieldType.RadioButtons,
          resetOnHide: true,
          defaultValue: false,
          props: {
            label: 'New build',
            required: true,
            options: yesNoOptions,
          },
          expressions: {
            hide: field => this.isRightToBuySelected(field),
          },
        },
        ...this.mortgageLenderFields(),
        {
          key: 'propertyType',
          type: FormFieldType.RadioButtons,
          defaultValue: PropertyType.House,
          resetOnHide: true,
          props: {
            label: 'Property type',
            options: [
              { label: 'House', value: PropertyType.House },
              { label: 'Flat', value: PropertyType.Flat },
            ],
            required: true,
          },
          expressions: {
            hide: field =>
              !field.model?.newBuild &&
              !this.helperService.isSharedOwnership(field.model?.productTypeExtended) &&
              (field.parent?.model?.productTypeExtended as ProductType) !== ProductType.RightToBuy,
          },
        },
        {
          key: 'propertyValue',
          type: FormFieldType.Currency,
          props: {
            label: 'Property value',
            required: true,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.productsSearchService.propertyValueDisabled,
          },
        },
        {
          key: 'purchasePriceShare',
          type: FormFieldType.Currency,
          resetOnHide: true,
          props: {
            label: 'Purchase price of share',
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
          expressions: {
            hide: field => !this.helperService.isSharedOwnership(field.parent?.model?.productTypeExtended),
          },
          hooks: {
            onInit: field => {
              if (field.formControl?.value === 0) {
                // Override default "Currency" control value to prevent usage of 0 value
                field.formControl.setValue(undefined);
              }
            },
          },
        },
        {
          key: 'discountedPurchasePrice',
          type: FormFieldType.Currency,
          resetOnHide: true,
          props: {
            label: 'Discounted purchase price',
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
          expressions: {
            hide: field =>
              !this.helperService.isPurchaseRightToBuy(field.parent?.model?.mortgageType, field.parent?.model?.productTypeExtended),
          },
          hooks: {
            onInit: field => {
              if (field.formControl?.value === 0) {
                // Override default "Currency" control value to prevent usage of 0 value
                field.formControl.setValue(undefined);
              }
            },
          },
        },
        {
          key: 'loanAmount',
          type: FormFieldType.Currency,
          props: {
            required: true,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.productsSearchService.loanAmountDisabled,
            'props.label': field => {
              return field.model?.mortgageType === MortgageType.SecondCharge ? secondChargeLoanAmountLabel : loanAmountLabel;
            },
            'props.helpText': field => {
              return this.helperService.isPurchaseRightToBuy(field.parent?.model?.mortgageType, field.parent?.model?.productTypeExtended)
                ? 'Include any borrowing above the discounted price'
                : null;
            },
          },
        },
        {
          key: 'additionalBorrowing',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Are additional funds required for home improvements?',
            options: yesNoOptions,
          },
          expressions: {
            hide: field =>
              !this.helperService.isPurchaseRightToBuy(field.parent?.model?.mortgageType, field.parent?.model?.productTypeExtended),
          },
        },
        {
          key: 'outstandingBalanceCurrentMortgage',
          type: FormFieldType.Currency,
          props: {
            required: true,
            label: 'Outstanding balance on the current mortgage',
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
          expressions: {
            hide: field => field.model?.mortgageType !== MortgageType.SecondCharge,
            'props.readonly': () => this.productsSearchService.loanAmountDisabled,
          },
        },
        {
          key: 'ltv',
          wrappers: [WrapperType.HtmlWrapper],
          expressions: {
            'props.html': field => {
              const ltv = this.helperService.isSharedOwnership(field.parent?.model?.productTypeExtended)
                ? this.ltvService.calculateSharePercent((field.parent?.model ?? {}) as IMortgageRequirements)
                : this.ltvService.calculateLtv((field.parent?.model ?? {}) as IMortgageRequirements);

              if (!ltv) {
                return '';
              }
              return `
                <div class="ltv-display">
                  ${this.ltvService.getPurchaseLTVFieldLabel(field.parent?.model?.productTypeExtended)}
                  ${ltv}%
                </div>
              `;
            },
          },
        },
        {
          key: 'lts',
          wrappers: [WrapperType.HtmlWrapper],
          expressions: {
            hide: field => !this.helperService.isSharedOwnership(field.parent?.model?.productTypeExtended),
            'props.html': field => {
              const lts = this.ltvService.calculateLts((field.parent?.model ?? {}) as IMortgageRequirements);

              if (!lts) {
                return '';
              }
              return `
                <div class="ltv-display">
                  LTV (purchase share)
                  ${lts}%
                </div>
              `;
            },
          },
        },
        {
          key: 'ltvDiscountedPrice',
          wrappers: [WrapperType.HtmlWrapper],
          expressions: {
            hide: field =>
              !this.helperService.isPurchaseRightToBuy(field.parent?.model?.mortgageType, field.parent?.model?.productTypeExtended),
            'props.html': field => {
              const ltvDisc = this.ltvService.calculateLtvDiscountedPrice((field.parent?.model ?? {}) as IMortgageRequirements);

              if (!ltvDisc) {
                return '';
              }

              return `
                <div class="ltv-display">
                  LTV (discounted price)
                  ${ltvDisc}%
                </div>
              `;
            },
          },
        },
        {
          key: 'helpToBuy',
          type: FormFieldType.RadioButtons,
          resetOnHide: true,
          defaultValue: null,
          props: {
            label: 'Help to buy',
            options: yesNoOptions,
            change: field =>
              (field.form as AbstractControl).patchValue({
                repaymentMethod: { value: field.model.helpToBuy ? RepaymentMethod.CapitalAndInterest : null },
              }),
          },
          expressions: {
            hide: field => this.isHelpToBuyHidden(field),
            'props.disabled': field => field.model.productTypeExtended === ProductType.RetirementInterestOnly,
          },
        },
        {
          key: 'repaymentMethod',
          defaultValue: null,
          props: {
            label: 'Repayment method',
            required: true,
            suppressErrors: true,
          },
          fieldGroup: [
            {
              key: 'value',
              type: FormFieldType.Select,
              props: {
                label: 'Repayment method',
                hideLabel: true,
                required: true,
                nested: true,
                allowNull: true,
                options: [
                  { label: 'Capital and interest', value: RepaymentMethod.CapitalAndInterest },
                  { label: 'Interest only', value: RepaymentMethod.InterestOnly },
                  { label: 'Interest only part and part', value: RepaymentMethod.InterestOnlyPartAndPart },
                ],
                collapseMargin: true,
              },
              expressions: {
                'props.readonly': field =>
                  this.isHelpToBuySelected(field.parent) ||
                  (field.parent?.parent?.formControl as UntypedFormGroup).controls?.productTypeExtended?.value ===
                    ProductType.RetirementInterestOnly,
              },
            },
          ],
        },
        {
          key: 'repaymentVehicle',
          type: FormFieldType.Select,
          defaultValue: null,
          props: {
            label: 'Repayment vehicle',
            options: [
              {
                value: RepaymentVehicle.SaleOfProperty,
                label: 'Sale of property',
              },
              {
                value: RepaymentVehicle.SaleOfSecondProperty,
                label: 'Sale of second property',
              },
              {
                value: RepaymentVehicle.EndowmentsInvestmentsPension,
                label: 'Endowments / Investments / Pension',
              },
              {
                value: RepaymentVehicle.SavingsISA,
                label: 'Savings / ISA\'s',
              },
            ],
            change: (field: FormlyFieldConfig) => {
              const propertyValue = Number(field.parent?.model?.propertyValue);
              const loanAmount = Number(field.parent?.model?.loanAmount);
              const equityValue = Number.isFinite(propertyValue) && Number.isFinite(loanAmount) ? propertyValue - loanAmount : null;

              (field.parent?.formControl as UntypedFormGroup).controls.equityValue?.setValue(
                equityValue,
              );
            },
          },
          expressions: {
            'props.disabled': () => false,
            hide: field =>
              (field.parent?.model as IMortgageRequirements)?.repaymentMethod?.value !== RepaymentMethod.InterestOnly &&
              (field.parent?.model as IMortgageRequirements)?.repaymentMethod?.value !== RepaymentMethod.InterestOnlyPartAndPart,
          },
        },
        {
          key: 'equityValue',
          type: FormFieldType.Currency,
          props: {
            label: 'How much equity is to remain in the property?',
            required: true,
          },
          expressions: {
            hide: field =>
              field.model?.repaymentVehicle !== RepaymentVehicle.SaleOfProperty ||
              field.model?.repaymentMethod.value === RepaymentMethod.CapitalAndInterest,
          },
          validators: {
            validation: [FormsValidators.integer, FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'interestOnlyAmount',
          type: FormFieldType.Currency,
          props: {
            label: 'Amount on interest only',
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          expressions: {
            hide: field =>
              (field.parent?.model as IMortgageRequirements)?.repaymentMethod?.value !== RepaymentMethod.InterestOnlyPartAndPart,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
        },
        {
          key: 'mortgageTerm',
          fieldGroupClassName: 'row',
          wrappers: ['horizontal-field'],
          props: {
            label: 'Mortgage term',
            suppressErrors: true,
            required: true,
          },
          fieldGroup: [
            {
              key: 'years',
              type: FormFieldType.Number,
              className: 'col',
              props: {
                label: 'Years',
                nested: true,
                required: true,
                min: 0,
                max: 40,
                groupedField: true,
                collapseMargin: true,
              },
              validation: [FormsValidators.integer],
            },
            {
              key: 'months',
              type: FormFieldType.Number,
              className: 'col',
              props: {
                label: 'Months',
                min: 0,
                max: 11,
                groupedField: true,
                collapseMargin: true,
              },
              validators: {
                validation: [FormsValidators.integer],
              },
            },
          ],
          validators: {
            max40Years: {
              expression: (control: AbstractControl) => {
                const { years, months } = control.value ?? {};
                return 12 * (years ?? 0) + (months ?? 0) <= 40 * 12;
              },
              message: 'Mortgage term has a maximum of 40 years',
            },
          },
        },
        {
          key: 'productLengths',
          type: FormFieldType.MultiSelect,
          expressions: {
            // Locked by the broker's firm: shown, populated, but not theirs to change. Readonly rather than
            // disabled - Formly implements disabled by disabling the FormControl, and Angular then leaves it out
            // of form.value, so the seeded value would never reach the search request.
            'props.readonly': () =>
              this.filterPreferenceLocks.isLocked('product', productFilterGroupTitles.initialProductPeriod),
            // Paired with the readonly above: a field that simply stops responding reads as a bug, so it has to
            // say who locked it. Both read the same lock, so the note cannot appear without the lock or vice versa.
            'props.lockedNote': () =>
              this.filterPreferenceLocks.isLocked('product', productFilterGroupTitles.initialProductPeriod)
                ? filterPreferenceLockedNote
                : null,
          },
          defaultValue: [],
          props: {
            label: productFiltersNames.initialProductPeriod,
            options: this.helperService.productLengthExtendedOptions(),
            required: false,
          },
        },
        {
          key: 'customProductLength',
          fieldGroupClassName: 'row',
          wrappers: ['horizontal-field'],
          props: {
            suppressErrors: true,
          },
          expressions: {
            hide: field => !(field.parent?.model as IMortgageRequirements)?.productLengths?.includes(ProductLengthExtended.Custom),
          },
          fieldGroup: [
            {
              key: 'from',
              type: FormFieldType.Number,
              className: 'col',
              name: 'From',
              wrappers: [WrapperType.HorizontalField],
              resetOnHide: true,
              props: {
                min: 0,
                label: 'From',
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
                required: true,
              },
              validation: [FormsValidators.integer],
            },
            {
              key: 'to',
              type: FormFieldType.Number,
              className: 'col',
              resetOnHide: true,
              wrappers: [WrapperType.HorizontalField],
              props: {
                min: 0,
                label: 'To',
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
                required: true,
              },
              validators: {
                validation: [FormsValidators.integer],
              },
            },
          ],
        },
        {
          key: 'rateTypes',
          type: FormFieldType.MultiSelect,
          expressions: {
            // Locked by the broker's firm: shown, populated, but not theirs to change. Readonly rather than
            // disabled - Formly implements disabled by disabling the FormControl, and Angular then leaves it out
            // of form.value, so the seeded value would never reach the search request.
            'props.readonly': () =>
              this.filterPreferenceLocks.isLocked('product', productFilterGroupTitles.rateType),
            // Paired with the readonly above: a field that simply stops responding reads as a bug, so it has to
            // say who locked it. Both read the same lock, so the note cannot appear without the lock or vice versa.
            'props.lockedNote': () =>
              this.filterPreferenceLocks.isLocked('product', productFilterGroupTitles.rateType)
                ? filterPreferenceLockedNote
                : null,
          },
          defaultValue: [],
          props: {
            label: 'Rate types',
            options: this.helperService.rateTypesOptions,
            required: false,
          },
        },
        {
          key: 'applicants',
          wrappers: [WrapperType.EmptyWrapper],
          fieldGroup: [
            {
              key: 'numberOfApplicants',
              type: FormFieldType.RadioButtons,
              defaultValue: defaultNumberOfApplicants,
              props: {
                label: 'Number of applicants',
                required: true,
                options: [
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                ],
                change: (field: FormlyFieldConfig) => {
                  const numberOfApplicants = +field.formControl?.value;
                  this.onNumberOfApplicantsChange(numberOfApplicants);
                },
              },
              hooks: {
                onInit: field => {
                  const existingValue = field.formControl?.value;

                  const existingApplicantsCount = field.parent?.model?.applicantAges?.length;

                  const numberOfApplicants = existingValue || existingApplicantsCount || defaultNumberOfApplicants;

                  if (field.formControl?.value !== numberOfApplicants) {
                    field.formControl?.setValue(numberOfApplicants);
                  }

                  this.onNumberOfApplicantsChange(numberOfApplicants);
                },
              },
            },
            {
              key: 'applicantAges',
              type: FormFieldType.Repeating,
              props: {
                suppressErrors: true,
                id: 'applicantAges',
              },
              fieldArray: {
                wrappers: [WrapperType.NoWrappers],
                props: {
                  suppressErrors: true,
                },
                fieldGroup: [
                  {
                    expressions: {
                      template: field => {
                        return `
                          <h2 class="fw-light mt-4 mb-3">Applicant ${field.model.applicantNumber}</h2>
                        `;
                      },
                    },
                  },
                  {
                    key: 'dateOfBirth',
                    type: FormFieldType.Date,
                    props: {
                      label: 'Date of birth',
                    },
                    validators: {
                      validation: [
                        FormsValidators.dateOrEmpty,
                        FormsValidators.atLeast18YearsOrEmpty,
                        FormsValidators.dateNotBefore126YearsAgo,
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
      ],
    },
    {
      key: 'getResultsBtn',
      type: FormFieldType.Buttons,
      props: {
        numColumns: 6,
        buttons: [{ label: 'Search', type: 'submit', className: 'btn-colour-blue-600' }],
      },
    },
  ];

  private onNumberOfApplicantsChange(numberOfApplicants: number) {
    if (!this.productsSearchService.model) {
      this.productsSearchService.updateModel();
    }

    const model = this.productsSearchService.model;

    if (model && !model.mortgageRequirements?.applicants) {
      model.mortgageRequirements.applicants = {} as IApplicants;
    }

    if (model?.mortgageRequirements.applicants && !model.mortgageRequirements.applicants?.applicantAges) {
      model.mortgageRequirements.applicants.applicantAges = this.helperService.generateEmptyApplicantAges(numberOfApplicants);
      this.productsSearchService.updateModel(this.productsSearchService.model);
      return;
    }

    const applicantAges = model?.mortgageRequirements.applicants?.applicantAges;

    if (model?.mortgageRequirements.applicants && applicantAges && applicantAges.length > numberOfApplicants) {
      model.mortgageRequirements.applicants.applicantAges = applicantAges?.slice(0, numberOfApplicants);
    } else if (applicantAges && applicantAges.length < numberOfApplicants) {
      const emptyApplicantAges = this.helperService.generateEmptyApplicantAges(
        numberOfApplicants - applicantAges.length,
        applicantAges.length,
      );
      model.mortgageRequirements.applicants?.applicantAges?.push(...emptyApplicantAges);
    }

    this.productsSearchService.updateModel(this.productsSearchService.model);
  }

  private getMortgageTypeOptions() {
    return [
      { label: 'Purchase', value: MortgageType.Purchase },
      { label: 'Remortgage', value: MortgageType.Remortgage },
      { label: 'Second charge', value: MortgageType.SecondCharge },
    ];
  }

  private mortgageLenderFields(): FormlyFieldConfig[] {
    const mortgageLenderHidden = (field: any) => {
      const mortgageType = field?.model?.mortgageType;
      const purchaserType = field?.model?.purchaserType;
      return !(
        mortgageType === MortgageType.Remortgage ||
        (mortgageType === MortgageType.Purchase && purchaserType === PurchaserType.HomeMover)
      );
    };

    const initialPeriodHidden = (field: any) => field?.model?.mortgageType !== MortgageType.Remortgage;

    return [
      {
        key: 'mortgageLender',
        wrappers: [WrapperType.EmptyWrapper],
        props: {
          suppressErrors: true,
        },
        fieldGroup: [
          {
            key: 'value',
            type: FormFieldType.Select,
            props: {
              label: 'Who is the current mortgage Lender?',
              allowNull: true,
            },
            expressions: {
              hide: field => mortgageLenderHidden(field.parent?.parent),
              'props.options': this.lenderService
                .getUnarchivedLendersNames(this.helperService.getResLendingType())
                .pipe(map(lenderNames => this.helperService.getMortgageLendersList(lenderNames))),
            },
          },
        ],
      },
      {
        key: 'currentMortgageLender',
        type: FormFieldType.Input,
        expressions: {
          hide: field => {
            if (mortgageLenderHidden(field)) {
              return true;
            }

            const mortgageLenderValue = field.model?.mortgageLender?.value;
            return !mortgageLenderValue || mortgageLenderValue !== MortgageLenders.Other;
          },
        },
      },
      {
        key: 'productTransferInitialDate',
        type: FormFieldType.Date,
        props: {
          label: 'What is the end date for the Initial period?',
        },
        validators: {
          validation: [FormsValidators.initialPeriodDate],
        },
        expressions: {
          hide: field => {
            if (initialPeriodHidden(field)) {
              return true;
            }

            return field.model?.mortgageLender?.value === MortgageLenders.Other;
          },
        },
      },
    ];
  }

  private isHelpToBuyHidden(field: FormlyFieldConfig | undefined): boolean {
    const mortgageType = field?.parent?.model?.mortgageType;
    if (mortgageType !== MortgageType.Remortgage && mortgageType !== MortgageType.Purchase) {
      return true;
    }

    return this.isRightToBuySelected(field);
  }

  private isRightToBuySelected(field: FormlyFieldConfig | undefined): boolean {
    const productType = field?.parent?.model?.productTypeExtended;
    return productType === ProductType.RightToBuy || productType === ProductType.RetirementInterestOnly;
  }

  private isHelpToBuySelected(field: FormlyFieldConfig | undefined) {
    const helpToBuy = !!field?.parent?.model?.helpToBuy;
    return !this.isHelpToBuyHidden(field) && helpToBuy;
  }
}
